import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import FieldTitle from './FieldTitle';
import { convertDraftJsToHtml } from '../../../lib/draftJsToHtml';
import Tooltip from '../../misc/Tooltip';

const StyledWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  ${(props) => (typeof props.margin === 'number' ? `padding: ${props.margin}px;` : '')}
`;

const StyledInternalWrapper = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const StyledTooltipWrapper = styled.span`
  position: absolute;
  right: 0;
  top: 0;
`;

const StyledContent = styled.div`
  height: 100%;
  width: 100%;
  background-color: ${(props) => props.backgroundColor};
  font-size: ${(props) => (props.fontSize !== undefined ? `${props.fontSize}px` : '')};
  color: ${(props) => props.fontColor};
  font-style: ${(props) => props.fontStyle || 'normal'};
  font-weight: ${(props) => props.fontWeight || 'normal'};
  text-align: ${(props) => props.textAlign || 'left'};
  text-transform: ${(props) => props.textTransform || 'none'};
  text-decoration: ${(props) => props.textDecoration || 'none'};
  ${(props) => (props.textOverflow ? `text-overflow: ${props.textOverflow};` : '')}
  overflow: ${(props) => (props.textOverflow ? 'hidden' : 'visible')};
  border-radius: ${(props) => (props.borderRadius !== undefined ? `${props.borderRadius}px` : '')};
  border: ${(props) => props.border};
  padding: ${(props) => (props.padding !== undefined ? `${props.padding}px` : '')};
  line-height: ${(props) => (props.lineHeight !== undefined ? `${props.lineHeight}px` : 'normal')};
`;

const StyledHtmlContent = styled.div`
  width: 100%;
  height: 100%;
  color: inherit;
  font: inherit;
  line-height: inherit;
  
  /* Style HTML content to match Draft.js formatting */
  p {
    margin: 0;
    margin-bottom: 0.5em;
    
    &:last-child {
      margin-bottom: 0;
    }
  }
  
  /* Ensure proper spacing for block elements */
  div {
    margin-bottom: 0.5em;
    
    &:last-child {
      margin-bottom: 0;
    }
  }
`;

class RTEEditor extends PureComponent {

  static propTypes = {
    fieldId: PropTypes.string,
    fieldIdentifier: PropTypes.string,
    fieldPositionId: PropTypes.string,

    title: PropTypes.string,
    tipTitle: PropTypes.string,
    tipDescription: PropTypes.string,
    tipVisible: PropTypes.bool,
    disabled: PropTypes.bool,

    wrapperStyles: PropTypes.object,

    // Title styles
    titleDisplay: PropTypes.string,
    titleFontSize: PropTypes.number,
    titleFontColor: PropTypes.string,
    titleTextAlign: PropTypes.string,
    titleFontStyle: PropTypes.string,
    titleFontWeight: PropTypes.string,
    titleTextTransform: PropTypes.string,
    titleTextDecoration: PropTypes.string,

    // Field style
    borderColor: PropTypes.string,
    borderRadius: PropTypes.number,
    borderWidth: PropTypes.number,
    backgroundColor: PropTypes.string,
    padding: PropTypes.number,
    margin: PropTypes.number,
    fontSize: PropTypes.number,
    fontColor: PropTypes.string,
    fontStyle: PropTypes.string,
    fontWeight: PropTypes.string,
    textAlign: PropTypes.string,
    textTransform: PropTypes.string,
    textDecoration: PropTypes.string,
    textOverflow: PropTypes.string,
    lineHeight: PropTypes.number,

    // Action handlers
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,

    value: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.object,
    ]),
  }

  static defaultProps = {
    value: '',
    lineHeight: undefined,
  }

  handleFocus = () => {
    const { disabled, onFocus, fieldId, fieldIdentifier, fieldPositionId } = this.props;
    if (!disabled && onFocus) {
      onFocus(fieldId, fieldIdentifier, fieldPositionId);
    }
  }

  handleBlur = () => {
    const { disabled, onBlur, fieldId, fieldIdentifier, fieldPositionId } = this.props;
    if (!disabled && onBlur) {
      onBlur(fieldId, fieldIdentifier, fieldPositionId, {});
    }
  }

  getDisplayValue = () => {
    const { value } = this.props;

    // Convert Draft.js format to HTML
    return convertDraftJsToHtml(value);
  }

  render() {
    const {
      wrapperStyles,
      fieldId,
      fieldPositionId,
      title,
      tipTitle,
      tipDescription,
      tipVisible,
      disabled,
      // Title styles
      titleDisplay,
      titleFontSize,
      titleFontColor,
      titleTextAlign,
      titleFontStyle,
      titleFontWeight,
      titleTextTransform,
      titleTextDecoration,
      // Field style
      fontSize,
      fontColor,
      fontStyle,
      fontWeight,
      textAlign,
      textTransform,
      textDecoration,
      textOverflow,
      lineHeight,
      borderColor,
      borderRadius,
      borderWidth,
      backgroundColor,
      padding,
      margin,
    } = this.props;

    const htmlContent = this.getDisplayValue();

    /**
     * We join width and color to a single border style because chrome displays
     * the border as a two tone color if you use border-color and border-width
     * individually.
     */
    const bWidth = borderWidth !== undefined ? `${borderWidth}px` : '0px';
    const bColor = borderColor || 'transparent';
    const border = `${bWidth} solid ${bColor}`;

    const getTooltip = () => {
      if (disabled) return null;

      return (
        <StyledTooltipWrapper>
          <Tooltip
            fieldId={fieldId}
            title={tipTitle}
            description={tipDescription}
            tipVisible={tipVisible}
          />
        </StyledTooltipWrapper>
      );
    };

    return (
      <StyledWrapper
        margin={margin}
        style={wrapperStyles || {}}
        data-testid={`${fieldPositionId || fieldId}-rte-field-wrapper`}
      >
        <StyledInternalWrapper>
          <FieldTitle
            {...{
              title,
              titleDisplay,
              titleFontSize,
              titleFontColor,
              titleTextAlign,
              titleFontStyle,
              titleFontWeight,
              titleTextTransform,
              titleTextDecoration,
            }}
          />
          {getTooltip()}
          <StyledContent
            border={border}
            {...{
              disabled,
              fontSize,
              fontColor,
              fontStyle,
              fontWeight,
              textAlign,
              textTransform,
              textDecoration,
              textOverflow,
              borderColor,
              borderRadius,
              borderWidth,
              backgroundColor,
              padding,
              lineHeight,
            }}
            onFocus={this.handleFocus}
            onBlur={this.handleBlur}
            tabIndex={disabled ? -1 : 0}
            data-testid={`${fieldPositionId || fieldId}-rte-field`}
          >
            {htmlContent ? (
              <StyledHtmlContent dangerouslySetInnerHTML={{ __html: htmlContent }} />
            ) : (
              <StyledHtmlContent>&nbsp;</StyledHtmlContent>
            )}
          </StyledContent>
        </StyledInternalWrapper>
      </StyledWrapper>
    );
  }

}

export default RTEEditor;
